/**
 * Community Spaces — role-separated moderated forums.
 *
 * Three views packaged here:
 *   - <CommunityLanding />      (/community)           list of 3 spaces + unread
 *   - <CommunitySpace />        (/community/:space)    threads list + topic filter
 *   - <CommunityThread />       (/community/:space/threads/:threadId)
 *
 * Calm professional styling — no leaderboards, no upvotes, no gamification.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useSafeBack } from "../hooks/useSafeBack";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, MessageCircle, Pin, Lock, Trash2, Send, Search,
  BadgeCheck, ShieldCheck, Star, Flag, X, ChevronRight, Users, Eye,
  BookOpen, Bell, BellOff, AlertTriangle,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ═══════════════════════════════════════════════════════════════
   SHARED HELPERS
   ═══════════════════════════════════════════════════════════════ */
function formatRelative(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

function AuthorTrustPills({ trust, compact = false }) {
  if (!trust) return null;
  const size = compact ? "w-2.5 h-2.5" : "w-3 h-3";
  const textSz = compact ? "text-[10px]" : "text-[11px]";
  return (
    <span className={`inline-flex items-center gap-1 ${textSz} text-slate-500 dark:text-slate-400`}>
      {trust.verified && (
        <span className="inline-flex items-center gap-0.5" title="Verified member">
          <BadgeCheck className={`${size} text-blue-500`} />
        </span>
      )}
      {trust.reliable_host && (
        <span className="inline-flex items-center gap-0.5 text-emerald-700 dark:text-emerald-400" title="Reliable host">
          <ShieldCheck className={size} />
        </span>
      )}
      {typeof trust.avg_rating === "number" && trust.rating_count >= 5 && (
        <span className="inline-flex items-center gap-0.5" title={`${trust.rating_count} learner reviews`}>
          <Star className={`${size} text-amber-500 fill-amber-500`} />
          <span className="font-semibold text-slate-600 dark:text-slate-300">
            {trust.avg_rating.toFixed(1)}
          </span>
        </span>
      )}
    </span>
  );
}

function AuthorLine({ author, createdAt }) {
  if (!author) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap text-[11px] text-slate-500 dark:text-slate-400">
      <span className="font-semibold text-slate-700 dark:text-slate-200">{author.name}</span>
      <AuthorTrustPills trust={author.trust} compact />
      {createdAt && (
        <>
          <span className="text-slate-300 dark:text-slate-600">·</span>
          <span>{formatRelative(createdAt)}</span>
        </>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   LANDING — /community
   ═══════════════════════════════════════════════════════════════ */
const SPACE_META = {
  learner: { tint: "from-blue-500 to-indigo-600", emoji: null, label: "Learner" },
  pdi: { tint: "from-amber-500 to-orange-600", emoji: null, label: "PDI" },
  adi: { tint: "from-emerald-500 to-teal-600", emoji: null, label: "ADI" },
};

export default function CommunityLanding() {
  const nav = useNavigate();
  const goBack = useSafeBack();
  const [spaces, setSpaces] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, u] = await Promise.all([
          axios.get(`${API_URL}/api/community/spaces`, { withCredentials: true }),
          axios.get(`${API_URL}/api/community/unread-counts`, { withCredentials: true }),
        ]);
        setSpaces(s.data?.spaces || []);
        setCounts(u.data?.counts || {});
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Couldn't load spaces");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div data-testid="community-landing" className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-slate-900">
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-2">
          <button
            type="button"
            onClick={() => goBack()}
            className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700"
            data-testid="community-back"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-slate-900 dark:text-white">Community Spaces</h1>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">
              Moderated rooms — learner, PDI, ADI. Useful discussions over noise.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-3">
        {loading ? (
          <div className="text-center py-12 text-sm text-slate-400">Loading…</div>
        ) : (
          spaces.map((s) => {
            const unread = counts[s.key] || 0;
            const meta = SPACE_META[s.key] || SPACE_META.learner;
            // can_read controls navigation; can_post shown as a read-only hint
            const canRead = s.can_read ?? s.accessible;
            const canPost = s.can_post ?? s.accessible;
            return (
              <button
                key={s.key}
                type="button"
                data-testid={`community-space-${s.key}`}
                disabled={!canRead}
                onClick={() => canRead && nav(`/community/${s.key}`)}
                className={`w-full text-left bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4 flex gap-4 items-center transition ${
                  canRead
                    ? "hover:border-slate-400 cursor-pointer"
                    : "opacity-60 cursor-not-allowed"
                }`}
              >
                <div
                  className={`w-12 h-12 rounded-xl bg-gradient-to-br ${meta.tint} text-white font-extrabold text-lg flex items-center justify-center flex-shrink-0`}
                >
                  {meta.label.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-sm font-bold text-slate-900 dark:text-white">{s.label}</h2>
                    {unread > 0 && (
                      <span
                        data-testid={`community-unread-${s.key}`}
                        className="text-[10px] font-bold uppercase tracking-wider rounded-full px-1.5 py-0.5 bg-indigo-100 text-indigo-700"
                      >
                        {unread} new
                      </span>
                    )}
                    {!canRead && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-400">
                        <Lock className="w-3 h-3" /> Members only
                      </span>
                    )}
                    {canRead && !canPost && (
                      <span
                        data-testid={`community-readonly-${s.key}`}
                        className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-500"
                      >
                        <Eye className="w-3 h-3" /> Read-only
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                    {s.description}
                  </p>
                  <p className="text-[11px] text-slate-400 mt-1">
                    <Users className="w-3 h-3 inline -mt-0.5 mr-0.5" />
                    {s.thread_count} {s.thread_count === 1 ? "thread" : "threads"} ·{" "}
                    {s.topics.length} topics
                  </p>
                </div>
                {canRead && <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   SPACE — /community/:space
   ═══════════════════════════════════════════════════════════════ */
export function CommunitySpace() {
  const { space } = useParams();
  const nav = useNavigate();
  const [spaceMeta, setSpaceMeta] = useState(null);
  const [topic, setTopic] = useState(null);
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newThreadOpen, setNewThreadOpen] = useState(false);
  const [me, setMe] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [meRes, spaceRes, threadsRes] = await Promise.all([
        axios.get(`${API_URL}/api/auth/me`, { withCredentials: true }),
        axios.get(`${API_URL}/api/community/spaces`, { withCredentials: true }),
        axios.get(
          `${API_URL}/api/community/space/${space}/threads${
            topic ? `?topic=${topic}` : ""
          }${q ? `${topic ? "&" : "?"}q=${encodeURIComponent(q)}` : ""}`,
          { withCredentials: true },
        ),
      ]);
      setMe(meRes.data);
      const sm = (spaceRes.data?.spaces || []).find((x) => x.key === space);
      setSpaceMeta(sm);
      setThreads(threadsRes.data?.threads || []);
      // Mark read after we've loaded threads (don't block UI)
      axios.post(
        `${API_URL}/api/community/space/${space}/mark-read`,
        {},
        { withCredentials: true },
      ).catch(() => {});
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error("This space is for members of that role only");
        nav("/community");
        return;
      }
      toast.error(err?.response?.data?.detail || "Couldn't load threads");
    } finally {
      setLoading(false);
    }
  }, [space, topic, q, nav]);

  useEffect(() => { load(); }, [load]);

  const submitSearch = (e) => {
    e.preventDefault();
    setQ(qInput.trim());
  };

  if (!space || !["learner", "pdi", "adi"].includes(space)) {
    return <div className="min-h-dvh no-overflow-x flex items-center justify-center text-sm text-slate-500">Unknown space</div>;
  }

  return (
    <div data-testid="community-space" className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-slate-900">
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-2">
          <button
            type="button"
            onClick={() => nav("/community")}
            className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700"
            data-testid="community-space-back"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-bold text-slate-900 dark:text-white truncate">
              {spaceMeta?.label || space}
            </h1>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
              {spaceMeta?.description}
            </p>
          </div>
          <button
            type="button"
            data-testid="community-new-thread-btn"
            onClick={() => setNewThreadOpen(true)}
            disabled={spaceMeta && !spaceMeta.can_post}
            title={spaceMeta && !spaceMeta.can_post ? "Read-only — you can only post in your own role's space" : ""}
            className="inline-flex items-center gap-1.5 text-xs font-bold bg-indigo-600 text-white rounded-lg px-3 py-2 hover:bg-indigo-700 disabled:bg-slate-300 disabled:cursor-not-allowed"
          >
            <MessageCircle className="w-3.5 h-3.5" />
            New thread
          </button>
        </div>

        {/* Topic filter tabs */}
        <div className="max-w-3xl mx-auto px-4 pb-3 flex gap-1.5 overflow-x-auto">
          <button
            type="button"
            data-testid="community-topic-all"
            onClick={() => setTopic(null)}
            className={`text-xs font-semibold rounded-lg px-3 py-1.5 whitespace-nowrap ${
              !topic
                ? "bg-indigo-600 text-white"
                : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200"
            }`}
          >
            All topics
          </button>
          {(spaceMeta?.topics || []).map((t) => (
            <button
              key={t.key}
              type="button"
              data-testid={`community-topic-${t.key}`}
              onClick={() => setTopic(t.key)}
              className={`text-xs font-semibold rounded-lg px-3 py-1.5 whitespace-nowrap ${
                topic === t.key
                  ? "bg-indigo-600 text-white"
                  : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="max-w-3xl mx-auto px-4 pb-3">
          <form onSubmit={submitSearch} className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="search"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="Search threads…"
              data-testid="community-search-input"
              className="w-full text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </form>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-5 space-y-2">
        {/* Pinned room guidance — house rules at the top of every space */}
        {spaceMeta?.guidance?.length > 0 && (
          <CommunityGuidance
            spaceKey={space}
            label={spaceMeta.label}
            guidance={spaceMeta.guidance}
            canPost={!!spaceMeta.can_post}
          />
        )}


        {newThreadOpen && (
          <NewThreadForm
            space={space}
            spaceMeta={spaceMeta}
            defaultTopic={topic || spaceMeta?.topics?.[0]?.key}
            onCancel={() => setNewThreadOpen(false)}
            onCreated={(t) => {
              setNewThreadOpen(false);
              load();
              nav(`/community/${space}/threads/${t.id}`);
            }}
          />
        )}

        {loading ? (
          <div className="text-center py-12 text-sm text-slate-400">Loading…</div>
        ) : threads.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700">
            <MessageCircle className="w-8 h-8 mx-auto text-slate-300 dark:text-slate-600 mb-2" />
            <p className="text-sm text-slate-500 dark:text-slate-400">
              No threads here yet.
            </p>
            <p className="text-xs text-slate-400 mt-1">Be the first to start one.</p>
          </div>
        ) : (
          threads.map((t) => (
            <ThreadRow
              key={t.id}
              thread={t}
              space={space}
              me={me}
              onChange={load}
            />
          ))
        )}
      </div>
    </div>
  );
}

function ThreadRow({ thread, space, me, onChange }) {
  const nav = useNavigate();
  const topicLabel = thread.topic
    ? thread.topic.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : "";
  const isAdmin = me?.role === "admin";
  const isAuthor = me?.id === thread.author_id;

  return (
    <div
      data-testid={`community-thread-row-${thread.id}`}
      className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 hover:border-slate-400 transition overflow-hidden"
    >
      <button
        type="button"
        onClick={() => nav(`/community/${space}/threads/${thread.id}`)}
        className="w-full text-left p-4"
      >
        <div className="flex items-center gap-2 flex-wrap mb-1">
          {thread.pinned && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-amber-100 text-amber-700">
              <Pin className="w-2.5 h-2.5" /> Pinned
            </span>
          )}
          {thread.locked && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-slate-200 text-slate-600">
              <Lock className="w-2.5 h-2.5" /> Locked
            </span>
          )}
          {topicLabel && (
            <span className="text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
              {topicLabel}
            </span>
          )}
        </div>
        <h3 className="text-sm font-bold text-slate-900 dark:text-white leading-snug">
          {thread.title}
        </h3>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">
          {thread.body_preview}
        </p>
        <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-500 dark:text-slate-400">
          <span className="font-semibold text-slate-700 dark:text-slate-200">
            {thread.author_name}
          </span>
          <span>·</span>
          <span>{formatRelative(thread.last_reply_at || thread.created_at)}</span>
          <span>·</span>
          <span className="inline-flex items-center gap-1">
            <MessageCircle className="w-3 h-3" />
            {thread.reply_count}
          </span>
        </div>
      </button>

      {(isAdmin || isAuthor) && (
        <div className="px-4 py-2 border-t border-slate-200 dark:border-slate-700 flex items-center gap-1 justify-end">
          {isAdmin && (
            <ModAction
              onClick={async () => {
                try {
                  await axios.post(
                    `${API_URL}/api/community/threads/${thread.id}/pin`,
                    {},
                    { withCredentials: true },
                  );
                  onChange();
                } catch { toast.error("Couldn't update"); }
              }}
              testid={`community-thread-pin-${thread.id}`}
              icon={Pin}
              label={thread.pinned ? "Unpin" : "Pin"}
            />
          )}
          {isAdmin && (
            <ModAction
              onClick={async () => {
                try {
                  await axios.post(
                    `${API_URL}/api/community/threads/${thread.id}/lock`,
                    {},
                    { withCredentials: true },
                  );
                  onChange();
                } catch { toast.error("Couldn't update"); }
              }}
              testid={`community-thread-lock-${thread.id}`}
              icon={Lock}
              label={thread.locked ? "Unlock" : "Lock"}
            />
          )}
          {(isAdmin || isAuthor) && (
            <ModAction
              onClick={async () => {
                if (!window.confirm("Delete this thread?")) return;
                try {
                  await axios.delete(
                    `${API_URL}/api/community/threads/${thread.id}`,
                    { withCredentials: true },
                  );
                  toast.success("Deleted");
                  onChange();
                } catch { toast.error("Couldn't delete"); }
              }}
              testid={`community-thread-delete-${thread.id}`}
              icon={Trash2}
              label="Delete"
              danger
            />
          )}
        </div>
      )}
    </div>
  );
}

function ModAction({ onClick, testid, icon: Icon, label, danger }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-[11px] font-semibold px-2 py-1 rounded-md inline-flex items-center gap-1 ${
        danger
          ? "text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20"
          : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
      }`}
    >
      <Icon className="w-3 h-3" />
      {label}
    </button>
  );
}

/**
 * CommunityGuidance — pinned room rules at the top of each space.
 * Collapsible so it doesn't dominate the scroll on return visits.
 * Calm professional tone — styled as a guidance card, not a warning.
 */
function CommunityGuidance({ spaceKey, label, guidance, canPost }) {
  const storageKey = `community-guidance-collapsed-${spaceKey}`;
  const [collapsed, setCollapsed] = useState(() => {
    try { return sessionStorage.getItem(storageKey) === "1"; }
    catch { return false; }
  });
  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    try { sessionStorage.setItem(storageKey, next ? "1" : "0"); } catch { /* noop */ }
  };
  return (
    <div
      data-testid={`community-guidance-${spaceKey}`}
      className="bg-slate-50 dark:bg-slate-800/60 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden"
    >
      <button
        type="button"
        onClick={toggle}
        data-testid={`community-guidance-toggle-${spaceKey}`}
        aria-expanded={!collapsed}
        className="w-full text-left px-4 py-3 flex items-center gap-2 hover:bg-slate-100 dark:hover:bg-slate-700/50"
      >
        <BookOpen className="w-4 h-4 text-slate-500" />
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
          {label} — house rules
        </span>
        {!canPost && (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-500 ml-1">
            <Eye className="w-3 h-3" /> Read-only for you
          </span>
        )}
        <ChevronRight
          className={`ml-auto w-4 h-4 text-slate-400 transition-transform ${
            collapsed ? "" : "rotate-90"
          }`}
        />
      </button>
      {!collapsed && (
        <ul className="px-4 pb-4 pt-1 space-y-1.5 text-[12px] leading-relaxed text-slate-600 dark:text-slate-300">
          {guidance.map((g, i) => (
            <li key={i} className="flex gap-2">
              <span className="text-slate-400 select-none">·</span>
              <span>{g}</span>
            </li>
          ))}
          <li className="flex gap-2 mt-2 pt-2 border-t border-slate-200 dark:border-slate-700 text-slate-500 italic">
            <Pin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-500" />
            <span>New here? Start with the pinned threads.</span>
          </li>
          {!canPost && (
            <li className="flex gap-2 text-slate-500">
              <Eye className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span className="italic">
                You can read this space but can only post in your own role's space.
              </span>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

function NewThreadForm({ space, spaceMeta, defaultTopic, onCancel, onCreated }) {
  const [topic, setTopic] = useState(defaultTopic || "");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setTopic(defaultTopic || ""); }, [defaultTopic]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/community/space/${space}/threads`,
        { topic, title: title.trim(), body: body.trim() },
        { withCredentials: true },
      );
      toast.success("Thread created");
      onCreated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't publish — check the fields");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="community-new-thread-form"
      className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4 space-y-3"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-900 dark:text-white">Start a thread</h3>
        <button
          type="button"
          onClick={onCancel}
          data-testid="community-new-thread-cancel"
          className="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700"
          aria-label="Cancel"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <select
        value={topic}
        onChange={(e) => setTopic(e.target.value)}
        required
        data-testid="community-new-thread-topic"
        className="w-full text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2"
      >
        <option value="" disabled>Pick a topic</option>
        {(spaceMeta?.topics || []).map((t) => (
          <option key={t.key} value={t.key}>{t.label}</option>
        ))}
      </select>

      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Thread title"
        required
        minLength={3}
        maxLength={160}
        data-testid="community-new-thread-title"
        className="w-full text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400"
      />
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="What's on your mind?"
        required
        rows={5}
        maxLength={8000}
        data-testid="community-new-thread-body"
        className="w-full text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none"
      />

      <button
        type="submit"
        disabled={saving || !topic || !title.trim() || !body.trim()}
        data-testid="community-new-thread-submit"
        className="w-full inline-flex items-center justify-center gap-2 bg-indigo-600 text-white font-bold text-sm rounded-xl px-4 py-3 hover:bg-indigo-700 disabled:bg-indigo-300"
      >
        {saving ? "Publishing…" : "Publish thread"}
      </button>
    </form>
  );
}

/* ═══════════════════════════════════════════════════════════════
   THREAD DETAIL — /community/:space/threads/:threadId
   ═══════════════════════════════════════════════════════════════ */
export function CommunityThread() {
  const { space, threadId } = useParams();
  const nav = useNavigate();
  const [thread, setThread] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyBody, setReplyBody] = useState("");
  const [sending, setSending] = useState(false);
  const [me, setMe] = useState(null);
  const [subscribed, setSubscribed] = useState(false);
  const [togglingSub, setTogglingSub] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [meRes, tRes, subRes] = await Promise.all([
        axios.get(`${API_URL}/api/auth/me`, { withCredentials: true }),
        axios.get(`${API_URL}/api/community/threads/${threadId}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/community/threads/${threadId}/subscription`, { withCredentials: true })
          .catch(() => ({ data: { subscribed: false } })),
      ]);
      setMe(meRes.data);
      setThread(tRes.data?.thread);
      setReplies(tRes.data?.replies || []);
      setSubscribed(!!subRes.data?.subscribed);
    } catch (err) {
      if (err?.response?.status === 404) {
        toast.error("Thread not found");
      } else {
        toast.error(err?.response?.data?.detail || "Couldn't load thread");
      }
      nav(`/community/${space}`);
    } finally {
      setLoading(false);
    }
  }, [threadId, space, nav]);

  useEffect(() => { load(); }, [load]);

  const toggleSubscribe = async () => {
    setTogglingSub(true);
    try {
      if (subscribed) {
        await axios.delete(
          `${API_URL}/api/community/threads/${threadId}/subscribe`,
          { withCredentials: true },
        );
        setSubscribed(false);
        toast.success("Unsubscribed — no more reply notifications for this thread");
      } else {
        await axios.post(
          `${API_URL}/api/community/threads/${threadId}/subscribe`,
          {},
          { withCredentials: true },
        );
        setSubscribed(true);
        toast.success("Subscribed — you'll see a notification on new replies");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update subscription");
    } finally {
      setTogglingSub(false);
    }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!replyBody.trim()) return;
    setSending(true);
    try {
      await axios.post(
        `${API_URL}/api/community/threads/${threadId}/reply`,
        { body: replyBody.trim() },
        { withCredentials: true },
      );      setReplyBody("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send reply");
    } finally {
      setSending(false);
    }
  };

  const reportContent = async (targetType, targetId) => {
    const reason = window.prompt("Why are you reporting this? (3-500 chars)");
    if (!reason || reason.trim().length < 3) return;
    try {
      await axios.post(
        `${API_URL}/api/community/report`,
        { target_type: targetType, target_id: targetId, reason: reason.trim() },
        { withCredentials: true },
      );
      toast.success("Thanks — we'll review");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't submit report");
    }
  };

  const deleteReply = async (rid) => {
    if (!window.confirm("Delete this reply?")) return;
    try {
      await axios.delete(
        `${API_URL}/api/community/replies/${rid}`,
        { withCredentials: true },
      );
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete");
    }
  };

  const isAdmin = me?.role === "admin";
  const isLocked = !!thread?.locked;
  // Replies are a posting action — strict role match (2c). Admin everywhere.
  const userRole = (me?.role || "").toLowerCase();
  const canPostHere = isAdmin || (!!thread && userRole === thread.space);
  const canReply = !!me && !isLocked && canPostHere;

  if (loading) {
    return <div className="min-h-dvh no-overflow-x flex items-center justify-center text-sm text-slate-400">Loading…</div>;
  }
  if (!thread) return null;

  return (
    <div data-testid="community-thread" className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-slate-900 pb-32">
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-2">
          <button
            type="button"
            onClick={() => nav(`/community/${space}`)}
            className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700"
            data-testid="community-thread-back"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-base font-bold text-slate-900 dark:text-white truncate flex-1">
            {thread.title}
          </h1>
          <button
            type="button"
            onClick={toggleSubscribe}
            disabled={togglingSub}
            data-testid="community-thread-subscribe"
            aria-pressed={subscribed}
            title={subscribed ? "You'll be notified of new replies" : "Get notified of new replies"}
            className={`inline-flex items-center gap-1 text-xs font-semibold rounded-lg px-2.5 py-1.5 border transition ${
              subscribed
                ? "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-200 border-indigo-200 dark:border-indigo-800"
                : "bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-200 border-slate-200 dark:border-slate-600 hover:bg-slate-50"
            }`}
          >
            {subscribed ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
            <span className="hidden sm:inline">{subscribed ? "Subscribed" : "Subscribe"}</span>
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-5 space-y-3">
        <article className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            {thread.pinned && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-amber-100 text-amber-700">
                <Pin className="w-2.5 h-2.5" /> Pinned
              </span>
            )}
            {thread.locked && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-slate-200 text-slate-600">
                <Lock className="w-2.5 h-2.5" /> Locked
              </span>
            )}
            <span className="text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
              {thread.topic?.replace(/_/g, " ")}
            </span>
            {isAdmin && thread.auto_flagged && (
              <span
                data-testid="community-thread-flagged"
                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-rose-100 text-rose-700"
                title={`Auto-flagged terms: ${(thread.flagged_terms || []).join(", ")}`}
              >
                <AlertTriangle className="w-2.5 h-2.5" /> Flagged
              </span>
            )}
          </div>
          <h2 className="text-lg font-extrabold text-slate-900 dark:text-white leading-tight">
            {thread.title}
          </h2>
          <div className="mt-2">
            <AuthorLine author={thread.author} createdAt={thread.created_at} />
          </div>
          <p
            data-testid="community-thread-body"
            className="mt-4 text-sm text-slate-700 dark:text-slate-300 whitespace-pre-line leading-relaxed"
          >
            {thread.body}
          </p>

          <div className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-700 flex items-center gap-1 flex-wrap">
            <button
              type="button"
              onClick={() => reportContent("thread", thread.id)}
              data-testid="community-thread-report"
              className="text-[11px] font-semibold text-slate-500 hover:text-slate-900 px-2 py-1 rounded-md inline-flex items-center gap-1"
            >
              <Flag className="w-3 h-3" />
              Report
            </button>
            {isAdmin && (
              <>
                <ModAction
                  onClick={async () => {
                    try {
                      await axios.post(
                        `${API_URL}/api/community/threads/${thread.id}/pin`,
                        {}, { withCredentials: true },
                      );
                      load();
                    } catch { toast.error("Couldn't update"); }
                  }}
                  testid="community-thread-detail-pin"
                  icon={Pin}
                  label={thread.pinned ? "Unpin" : "Pin"}
                />
                <ModAction
                  onClick={async () => {
                    try {
                      await axios.post(
                        `${API_URL}/api/community/threads/${thread.id}/lock`,
                        {}, { withCredentials: true },
                      );
                      load();
                    } catch { toast.error("Couldn't update"); }
                  }}
                  testid="community-thread-detail-lock"
                  icon={Lock}
                  label={thread.locked ? "Unlock" : "Lock"}
                />
              </>
            )}
          </div>
        </article>

        <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 pt-2">
          {replies.length} {replies.length === 1 ? "reply" : "replies"}
        </h3>

        {replies.map((r) => {
          const isAuthor = me?.id === r.author?.id;
          return (
            <div
              key={r.id}
              data-testid={`community-reply-${r.id}`}
              className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4"
            >
              <AuthorLine author={r.author} createdAt={r.created_at} />
              {isAdmin && r.auto_flagged && (
                <span
                  data-testid={`community-reply-flagged-${r.id}`}
                  className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-rose-100 text-rose-700"
                  title={`Auto-flagged terms: ${(r.flagged_terms || []).join(", ")}`}
                >
                  <AlertTriangle className="w-2.5 h-2.5" /> Flagged
                </span>
              )}
              <p className="mt-2 text-sm text-slate-700 dark:text-slate-300 whitespace-pre-line leading-relaxed">
                {r.body}
              </p>
              <div className="mt-2 flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => reportContent("reply", r.id)}
                  data-testid={`community-reply-report-${r.id}`}
                  className="text-[11px] font-semibold text-slate-500 hover:text-slate-900 px-2 py-1 rounded-md inline-flex items-center gap-1"
                >
                  <Flag className="w-3 h-3" />
                  Report
                </button>
                {(isAdmin || isAuthor) && (
                  <ModAction
                    onClick={() => deleteReply(r.id)}
                    testid={`community-reply-delete-${r.id}`}
                    icon={Trash2}
                    label="Delete"
                    danger
                  />
                )}
              </div>
            </div>
          );
        })}

        {replies.length === 0 && (
          <div className="text-center py-6 text-xs text-slate-400">
            No replies yet. Be the first to add one.
          </div>
        )}
      </div>

      {/* Reply composer — sticky bottom */}
      {canReply ? (
        <form
          onSubmit={sendReply}
          data-testid="community-reply-form"
          className="fixed bottom-0 inset-x-0 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 px-4 py-3 pb-safe"
        >
          <div className="max-w-3xl mx-auto flex items-end gap-2">
            <textarea
              value={replyBody}
              onChange={(e) => setReplyBody(e.target.value)}
              placeholder="Write a reply…"
              rows={2}
              maxLength={8000}
              data-testid="community-reply-body"
              className="flex-1 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
            <button
              type="submit"
              disabled={sending || !replyBody.trim()}
              data-testid="community-reply-send"
              className="inline-flex items-center gap-1.5 bg-indigo-600 text-white font-bold text-sm rounded-xl px-4 py-3 hover:bg-indigo-700 disabled:bg-indigo-300"
            >
              <Send className="w-3.5 h-3.5" />
              {sending ? "…" : "Reply"}
            </button>
          </div>
        </form>
      ) : isLocked ? (
        <div className="fixed bottom-0 inset-x-0 bg-slate-100 dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 px-4 py-3 pb-safe text-center text-xs text-slate-500">
          <Lock className="w-3 h-3 inline -mt-0.5 mr-1" /> This thread is locked — no new replies.
        </div>
      ) : !canPostHere ? (
        <div
          data-testid="community-thread-readonly-banner"
          className="fixed bottom-0 inset-x-0 bg-slate-50 dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 px-4 py-3 pb-safe text-center text-xs text-slate-500"
        >
          <Eye className="w-3 h-3 inline -mt-0.5 mr-1" /> You can read this space — posting is for {thread?.space?.toUpperCase()}s only.
        </div>
      ) : null}
    </div>
  );
}
